import React, { useEffect, useRef, useState } from "react";
import {
  LockKeyhole,
  Pause,
  Play,
  RefreshCw,
  RotateCcw,
  RotateCw,
} from "lucide-react";
import asendiaLogo from "../../assets/images/asendia_logos/logo-mark-transparent-512.png";
import { fetchReview, formatTime, mediaUrl } from "./reviewApi";
import "./review.css";
import "./listen.css";

const RATES = [0.75, 1, 1.25, 1.5, 2];

/**
 * Client-facing audio page for /share/listen/<token>.
 *
 * The token is audio-only on the server: the API answers it with the
 * candidate's name, role and one playable source and nothing else, so this
 * page never has a transcript, questions or video to show.
 */
export default function ListenInterviewShare({ token, initialRequest }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [mediaStatus, setMediaStatus] = useState("loading");
  const [rate, setRate] = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  const mediaRef = useRef(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const previous = document.title;
    document.title = "Interview audio | Asendia AI";
    return () => {
      document.title = previous;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const request = initialRequest
      ? initialRequest
      : fetchReview(token).then(
          (value) => ({ data: value }),
          (reason) => ({ error: reason }),
        );
    request.then((outcome) => {
      if (cancelled) return;
      if (outcome.error) setError(outcome.error);
      else setData(outcome.data);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [token, initialRequest]);

  const candidate = data?.candidate || {};
  const source = mediaUrl(candidate.audioUrl);
  const company = data?.branding?.companyName || "";

  function play() {
    const media = mediaRef.current;
    if (!media) return;
    const attempt = media.play();
    if (attempt && typeof attempt.catch === "function")
      attempt.catch((reason) => {
        // Pausing before buffering finishes rejects the pending play() with
        // AbortError; that is not a loading failure.
        if (reason?.name === "AbortError") return;
        setMediaStatus("error");
      });
  }

  function seek(time) {
    const media = mediaRef.current;
    if (!media) return;
    const limit = Number.isFinite(media.duration) ? media.duration : duration;
    const floor = Math.max(0, time);
    const next = limit > 0 ? Math.min(floor, limit) : floor;
    media.currentTime = next;
    setCurrentTime(next);
  }

  function ready() {
    const media = mediaRef.current;
    if (!media) return;
    if (Number.isFinite(media.duration)) setDuration(media.duration);
    media.playbackRate = rate;
    // preload="metadata" may stop at HAVE_METADATA (1): the duration is known
    // and play() will fetch the rest, so the controls can be enabled.
    setMediaStatus(media.readyState >= 1 ? "ready" : "loading");
  }

  function changeRate(value) {
    setRate(value);
    if (mediaRef.current) mediaRef.current.playbackRate = value;
  }

  async function refreshMedia() {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const fresh = await fetchReview(token);
      if (!mounted.current) return;
      setData(fresh);
      setMediaStatus("loading");
      if (mediaRef.current) mediaRef.current.load();
    } catch (e) {
      if (!mounted.current) return;
      if (e.status === 404 || e.status === 410) {
        setData(null);
        setError(e);
      } else setMediaStatus("error");
    } finally {
      if (mounted.current) setRefreshing(false);
    }
  }

  function keyboard(event) {
    // Native controls (the seek slider, speed select, buttons) keep their own
    // keyboard behaviour; the shortcuts only apply to the card itself.
    if (
      event.target.closest("button, select, input, a") ||
      event.altKey ||
      event.ctrlKey ||
      event.metaKey
    )
      return;
    if (event.key === " ") {
      event.preventDefault();
      playing ? mediaRef.current?.pause() : play();
    }
    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      seek(currentTime + (event.key === "ArrowLeft" ? -10 : 10));
    }
  }

  const progress = duration ? Math.min(100, (currentTime / duration) * 100) : 0;
  const statusText =
    mediaStatus === "error"
      ? "The recording could not load."
      : mediaStatus === "loading"
        ? "Loading recording…"
        : playing
          ? "Playing"
          : "Ready to play";

  return (
    <div className="review-page listen-page">
      <header className="review-header">
        <a
          className="review-brand"
          href="https://www.asendia.ai"
          target="_blank"
          rel="noreferrer"
          aria-label="Asendia AI website"
        >
          <img src={asendiaLogo} alt="" />
          Asendia AI
        </a>
        <span className="review-header-label">Interview audio</span>
        <span className="review-private">
          <LockKeyhole size={12} aria-hidden="true" />
          Private link
        </span>
      </header>
      <main className="listen-main">
        {loading ? (
          <p role="status" className="listen-status">
            Loading…
          </p>
        ) : error || !data ? (
          <section className="listen-card listen-card-empty" aria-label="Link unavailable">
            <h1>This link is no longer available</h1>
            <p>
              {error?.status === 404 || error?.status === 410
                ? "The interview link has expired or was revoked. Please ask the recruiting team for a new one."
                : "The interview could not be loaded. Please try again in a moment."}
            </p>
            {!(error?.status === 404 || error?.status === 410) && (
              <button className="review-button" onClick={() => window.location.reload()}>
                Try again
              </button>
            )}
          </section>
        ) : (
          <section
            className={`listen-card ${playing ? "is-playing" : ""}`}
            aria-label="Interview audio player"
            aria-describedby="listen-keyboard-help"
            tabIndex={0}
            onKeyDown={keyboard}
          >
            <p id="listen-keyboard-help" className="review-sr-only">
              With this card focused, press Space to play or pause. Use the
              left and right arrow keys to skip 10 seconds.
            </p>
            <p className="listen-eyebrow">
              {company ? `${company} · ` : ""}Interview audio
            </p>
            <h1 className="listen-name">{candidate.displayName || "Candidate"}</h1>
            {candidate.jobTitle && (
              <p className="listen-role">{candidate.jobTitle}</p>
            )}

            {source ? (
              <>
                <audio
                  ref={mediaRef}
                  src={source}
                  preload="metadata"
                  aria-label="Interview audio"
                  onLoadedMetadata={ready}
                  onCanPlay={ready}
                  onTimeUpdate={() =>
                    setCurrentTime(mediaRef.current?.currentTime || 0)
                  }
                  onDurationChange={ready}
                  onPlay={() => setPlaying(true)}
                  onPause={() => setPlaying(false)}
                  onEnded={() => setPlaying(false)}
                  onError={() => setMediaStatus("error")}
                />
                <div className="listen-wave" aria-hidden="true">
                  {Array.from({ length: 28 }, (_, i) => (
                    <span key={i} style={{ "--i": i }} />
                  ))}
                </div>
                <div className="listen-controls">
                  <button
                    type="button"
                    className="listen-skip"
                    onClick={() => seek(currentTime - 10)}
                    aria-label="Back 10 seconds"
                    disabled={mediaStatus !== "ready"}
                  >
                    <RotateCcw size={18} aria-hidden="true" />
                    <span>10</span>
                  </button>
                  <button
                    type="button"
                    className="listen-play"
                    onClick={() => (playing ? mediaRef.current?.pause() : play())}
                    aria-label={playing ? "Pause" : "Play"}
                    disabled={mediaStatus === "error"}
                  >
                    {playing ? (
                      <Pause size={26} fill="currentColor" aria-hidden="true" />
                    ) : (
                      <Play size={26} fill="currentColor" aria-hidden="true" />
                    )}
                  </button>
                  <button
                    type="button"
                    className="listen-skip"
                    onClick={() => seek(currentTime + 10)}
                    aria-label="Forward 10 seconds"
                    disabled={mediaStatus !== "ready"}
                  >
                    <RotateCw size={18} aria-hidden="true" />
                    <span>10</span>
                  </button>
                </div>
                <div className="listen-timeline">
                  <span className="listen-time">{formatTime(currentTime)}</span>
                  <input
                    type="range"
                    min="0"
                    max={duration || 0}
                    step="0.1"
                    value={Math.min(currentTime, duration || 0)}
                    onChange={(event) => seek(Number(event.target.value))}
                    aria-label="Seek"
                    aria-valuetext={`${formatTime(currentTime)} of ${formatTime(duration)}`}
                    style={{ "--progress": `${progress}%` }}
                    disabled={!duration}
                  />
                  <span className="listen-time">{formatTime(duration)}</span>
                </div>
                <div className="listen-meta">
                  <span
                    className={`listen-state ${mediaStatus === "error" ? "is-error" : ""}`}
                    role="status"
                  >
                    {statusText}
                  </span>
                  <span className="listen-meta-actions">
                    <label className="listen-rate">
                      <span className="review-sr-only">Playback speed</span>
                      <select
                        value={rate}
                        onChange={(event) => changeRate(Number(event.target.value))}
                        aria-label="Playback speed"
                      >
                        {RATES.map((value) => (
                          <option key={value} value={value}>
                            {value}×
                          </option>
                        ))}
                      </select>
                    </label>
                    <button
                      type="button"
                      className="review-icon-button"
                      onClick={refreshMedia}
                      disabled={refreshing}
                      aria-label={refreshing ? "Retrying recording" : "Retry recording"}
                      title="Retry recording"
                    >
                      <RefreshCw
                        size={15}
                        className={refreshing ? "review-spin" : ""}
                        aria-hidden="true"
                      />
                    </button>
                  </span>
                </div>
              </>
            ) : (
              <div className="listen-pending">
                <h2>Recording is not available yet</h2>
                <p>Check again shortly without leaving this page.</p>
                {mediaStatus === "error" && (
                  <p className="listen-state is-error" role="status">
                    The link could not be refreshed. Please try again.
                  </p>
                )}
                <button
                  className="review-button"
                  disabled={refreshing}
                  onClick={refreshMedia}
                >
                  Check again
                </button>
              </div>
            )}
          </section>
        )}
        <footer className="review-footer listen-footer">
          <LockKeyhole size={12} aria-hidden="true" />
          Confidential candidate information · Only share this link with
          authorized reviewers.
        </footer>
      </main>
    </div>
  );
}
